<template>
  <CCard class="shadow-sm rounded-3 bg-white">
    <CCardHeader style="background-color: #145654"
      class="border-bottom d-flex justify-content-between align-items-center">
      <h4 class="card-title mb-0 text-white">
        <CIcon name="cil-building" size="xl" /> {{ $t('buildingMgmt.pageTitle') }}
      </h4>
      <div class="d-flex align-items-center gap-2 btn-add-mobile">
        <CButton
          v-if="role === 'admin'"
          color="primary"
          shape="pill"
          class="mr-2"
          @click="triggerBuildingsImport"
        >
          <CIcon name="cil-file" />
          &nbsp;{{ importBuildingsLabel }}
        </CButton>
        <CButton
          v-if="role === 'admin'"
          color="info"
          shape="pill"
          class="mr-2"
          :disabled="groupedBuildingsData.length === 0"
          @click="downloadBuildingsXlsx"
        >
          <CIcon name="cil-cloud-download" />
          &nbsp;{{ downloadBuildingsLabel }}
        </CButton>
        <CButton v-if="selectedBuildings.length > 0" color="danger" shape="pill" @click="deleteMultipleBuildings()">
          <CIcon name="cil-trash" /> {{ $t('staffMgmt.deleteSelected') }} ({{ selectedBuildings.length }})
        </CButton>
        <CButton color="success" shape="pill" @click="openAddModal()">
          <CIcon name="cil-plus" />
          &nbsp;{{ $t('buildingMgmt.addRoom') }}
        </CButton>
        <input
          ref="buildingsImportInput"
          type="file"
          accept=".xlsx"
          class="d-none"
          @change="handleBuildingsImport"
        />
      </div>
    </CCardHeader>

    <CCardBody>
      <CDataTable class="custom-pagination" :items="groupedBuildingsData" :fields="buildingFields" table-filter hover
        pagination>
        <template #select-header>
          <input type="checkbox" @change="toggleSelectAll($event.target.checked)" :checked="selectedBuildings.length === groupedBuildingsData.length && groupedBuildingsData.length > 0" style="cursor: pointer; width: 1.2em; height: 1.2em;" />
        </template>

        <template #select="{ item }">
          <td class="text-center align-middle">
            <input type="checkbox" :value="item._id" v-model="selectedBuildings" style="cursor: pointer; width: 1.2em; height: 1.2em;" />
          </td>
        </template>

        <template #buildingCode="{ item }">
          <td class="p-2 align-middle">
            <strong style="white-space: normal; word-break: break-word;">{{ item.buildingCode }}</strong>
          </td>
        </template>

        <template #actions="{ item }">
          <td class="p-2 text-center align-middle">
            <div class="d-flex justify-content-center">
              <CButton size="sm" color="warning" variant="ghost" shape="pill" class="mr-1"
                @click="openEditBuildingModal(item)"
                v-c-tooltip="$t('buildingMgmt.editTooltip')">
                <CIcon name="cil-pencil" />
              </CButton>
              <CButton size="sm" color="danger" variant="ghost" shape="pill" 
                @click="confirmDelete(item)"
                v-c-tooltip="$t('buildingMgmt.deleteBuildingTooltip')">
                <CIcon name="cil-trash" />
              </CButton>
            </div>
          </td>
        </template>

        <template #rooms="{ item }">
          <td class="p-0">
            <table class="table table-sm table-bordered mb-0 fixed-table">
              <tbody>
                <tr v-for="room in item.rooms" :key="room.ROOMID">
                  <td>{{ room.ROOMNAME }}</td>
                </tr>
              </tbody>
            </table>
          </td>
        </template>
      </CDataTable>


      <CModal :title="$t('buildingMgmt.editRoomModal')" size="md" :show.sync="editModal">
        <div>
          <CInput :label="$t('buildingMgmt.newRoomName')" v-model="editingRoomName" />
        </div>
        <template #footer>
          <CButton color="secondary" @click="editModal = false">{{ $t('mgmt.cancel') }}</CButton>
          <CButton color="warning" @click="submitEdit">{{ $t('buildingMgmt.saveEditBuilding') }}</CButton>
        </template>
      </CModal>


      <CModal :title="$t('buildingMgmt.addBuildingModal')" size="md" :show.sync="addModal">
        <div>
          <CInput :label="$t('buildingMgmt.buildingName')" :placeholder="$t('buildingMgmt.buildingPh')" v-model="newBuildingCode" />
          <CInput :label="$t('buildingMgmt.roomName')" :placeholder="$t('buildingMgmt.roomPh')" v-model="newRoomName" />
        </div>

        <template #footer>
          <CButton shape="pill" color="secondary" @click="addModal = false">{{ $t('mgmt.cancel') }}</CButton>
          <CButton shape="pill" color="success" @click="addDepartment">{{ $t('mgmt.add') }}</CButton>
        </template>
      </CModal>

      <CModal :title="$t('buildingMgmt.editDataModal')" size="md" :show.sync="editBuildingModal">
        <div class="mb-3">
          <CInput :label="$t('buildingMgmt.buildingName')" v-model="editingNewBuildingName" />
        </div>
        <div>
          <div class="d-flex justify-content-between align-items-center mb-2">
            <label class="mb-0">{{ $t('buildingMgmt.roomNamesLabel') }}</label>
            <CButton v-if="selectedRoomsToDelete.length > 0" color="danger" size="sm" @click="deleteSelectedRoomsInModal()">
               {{ $t('staffMgmt.deleteSelected') }} ({{ selectedRoomsToDelete.length }})
            </CButton>
          </div>
          <div v-for="(room, index) in editingRooms" :key="index" v-show="!room.isDeleted" class="mb-2 d-flex align-items-center">
             <input type="checkbox" class="mr-2" :value="index" v-model="selectedRoomsToDelete" style="cursor: pointer; width: 1.2em; height: 1.2em;" />
             <CInput v-model="room.ROOMNAME" :placeholder="$t('buildingMgmt.roomInputPh')" class="mb-0 flex-grow-1" style="width: 100%;">
               <template #prepend-content><CIcon name="cil-room"/></template>
             </CInput>
          </div>
          <CButton color="success" size="sm" class="mt-2" @click="addEmptyRoom"><CIcon name="cil-plus"/> {{ $t('buildingMgmt.addRoomBtn') }}</CButton>
        </div>
        <template #footer>
          <CButton color="secondary" @click="editBuildingModal = false">{{ $t('mgmt.cancel') }}</CButton>
          <CButton color="warning" @click="submitEditBuilding">{{ $t('buildingMgmt.saveEditBuilding') }}</CButton>
        </template>
      </CModal>

      <CModal :title="importPreviewTitle" size="lg" :show.sync="showImportPreview" :close-on-backdrop="false">
        <div class="mb-2 text-muted">
          {{ importFileName }} — {{ importPreviewSummary }}
        </div>
        <CDataTable
          :items="importPreviewRows"
          :fields="importPreviewFields"
          hover
          striped
          responsive
          pagination
          :items-per-page="10"
        >
          <template #status="{ item }">
            <td>
              <CBadge :color="item.statusColor">{{ item.status }}</CBadge>
            </td>
          </template>
        </CDataTable>
        <template #footer>
          <CButton color="secondary" shape="pill" :disabled="isImporting" @click="closeImportPreview">
            {{ $t('mgmt.cancel') }}
          </CButton>
          <CButton
            color="success"
            shape="pill"
            :disabled="isImporting || importableRows.length === 0"
            @click="confirmBuildingsImport"
          >
            <span v-if="isImporting" class="spinner-border spinner-border-sm mr-1"></span>
            {{ confirmImportLabel }} ({{ importableRows.length }})
          </CButton>
        </template>
      </CModal>
    </CCardBody>
  </CCard>
</template>



<script>
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

export default {
  data() {
    return {
      selectedBuildings: [],
      selectedRoomsToDelete: [],
      addModal: false,
      newBuildingCode: "",
      newRoomName: "",
      editModal: false,
      editingRoomId: "",
      editingRoomName: "",
      editBuildingModal: false,
      editingOldBuildingName: "",
      editingNewBuildingName: "",
      editingRooms: [],
      showImportPreview: false,
      importPreviewRows: [],
      importFileName: "",
      isImporting: false
    };
  },

  computed: {
    ...mapGetters("building", ["buildings", "loading"]),
    ...mapGetters("auth", ["role"]),

    downloadBuildingsLabel() {
      return this.$store.state.locale.lang === "en"
        ? "Download buildings (.xlsx)"
        : "ดาวน์โหลดอาคาร (.xlsx)";
    },

    importBuildingsLabel() {
      return this.$store.state.locale.lang === "en"
        ? "Import buildings (.xlsx)"
        : "นำเข้าอาคาร (.xlsx)";
    },

    importPreviewTitle() {
      return this.$store.state.locale.lang === "en"
        ? "Preview building import"
        : "ตรวจสอบข้อมูลก่อนนำเข้าอาคาร";
    },

    confirmImportLabel() {
      return this.$store.state.locale.lang === "en" ? "Import" : "นำเข้า";
    },

    importableRows() {
      return this.importPreviewRows.filter((row) => row.importable);
    },

    importPreviewSummary() {
      const ready = this.importableRows.length;
      const skipped = this.importPreviewRows.length - ready;
      return this.$store.state.locale.lang === "en"
        ? `${ready} ready, ${skipped} skipped`
        : `พร้อมนำเข้า ${ready} รายการ, ข้าม ${skipped} รายการ`;
    },

    groupedBuildingsData() {
      return this.buildings;
    },

    buildingFields() {
      return [
        { key: "select", label: "", _style: 'width: 1%; text-align: center;', sorter: false, filter: false },
        { key: "buildingCode", label: this.$t("buildingMgmt.buildingCol"), _style: 'width: 40%' },
        { key: "rooms", label: this.$t("buildingMgmt.roomCol"), _style: 'width: 50%' },
        { key: "actions", label: this.$t("mgmt.actions"), _style: 'width: 10%; text-align: center', sorter: false, filter: false },
      ];
    },

    importPreviewFields() {
      return [
        { key: "buildingCode", label: "BUILDINGCODE" },
        { key: "roomName", label: "ROOMNAME" },
        {
          key: "status",
          label: this.$store.state.locale.lang === "en" ? "Status" : "สถานะ",
          sorter: false,
          filter: false
        }
      ];
    },
  },

  methods: {
    triggerBuildingsImport() {
      if (this.role !== "admin") return;
      this.$refs.buildingsImportInput.value = "";
      this.$refs.buildingsImportInput.click();
    },

    readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = () => reject(reader.error || new Error("Could not read file"));
        reader.readAsArrayBuffer(file);
      });
    },

    async handleBuildingsImport(event) {
      const file = event.target.files && event.target.files[0];
      if (!file || this.role !== "admin") return;

      try {
        if (!file.name.toLowerCase().endsWith(".xlsx")) {
          throw new Error(
            this.$store.state.locale.lang === "en"
              ? "Only .xlsx files are supported"
              : "รองรับเฉพาะไฟล์ .xlsx เท่านั้น"
          );
        }

        const data = await this.readFileAsArrayBuffer(file);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) throw new Error("Workbook has no worksheet");

        const worksheet = workbook.Sheets[firstSheetName];
        const table = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          raw: false,
          blankrows: false
        });

        if (table.length === 0) {
          throw new Error(
            this.$store.state.locale.lang === "en" ? "The file is empty" : "ไฟล์ไม่มีข้อมูล"
          );
        }

        const headers = table[0].map((value) =>
          String(value || "").replace(/^\uFEFF/, "").trim().toUpperCase()
        );
        const buildingIndex = headers.indexOf("BUILDINGCODE");
        const roomIndex = headers.indexOf("ROOMNAME");

        if (buildingIndex === -1 || roomIndex === -1) {
          throw new Error(
            this.$store.state.locale.lang === "en"
              ? "The file must contain BUILDINGCODE and ROOMNAME columns"
              : "ไฟล์ต้องมีคอลัมน์ BUILDINGCODE และ ROOMNAME"
          );
        }

        this.importFileName = file.name;
        this.importPreviewRows = this.buildImportPreviewRows(
          table.slice(1),
          buildingIndex,
          roomIndex
        );

        if (this.importPreviewRows.length === 0) {
          throw new Error(
            this.$store.state.locale.lang === "en"
              ? "No building rows were found"
              : "ไม่พบรายการอาคารในไฟล์"
          );
        }

        this.showImportPreview = true;
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: this.$store.state.locale.lang === "en" ? "Invalid import file" : "ไฟล์นำเข้าไม่ถูกต้อง",
          text: error.message
        });
      } finally {
        event.target.value = "";
      }
    },

    buildImportPreviewRows(dataRows, buildingIndex, roomIndex) {
      const existingRoomToBuilding = new Map();
      this.groupedBuildingsData.forEach((building) => {
        (building.rooms || []).forEach((room) => {
          existingRoomToBuilding.set(
            String(room.ROOMNAME || "").trim().toLowerCase(),
            String(building.buildingCode || "").trim().toLowerCase()
          );
        });
      });

      const seenRooms = new Set();

      return dataRows
        .map((row, index) => {
          const buildingCode = String(row[buildingIndex] || "").trim();
          const roomName = String(row[roomIndex] || "").trim();
          return { buildingCode, roomName, sourceRow: index + 2 };
        })
        .filter(({ buildingCode, roomName }) => buildingCode || roomName)
        .map((row) => {
          const roomKey = row.roomName.toLowerCase();
          const buildingKey = row.buildingCode.toLowerCase();
          let status;
          let statusColor;
          let importable = false;

          if (!row.buildingCode || !row.roomName) {
            status = this.$store.state.locale.lang === "en" ? "Missing data" : "ข้อมูลไม่ครบ";
            statusColor = "danger";
          } else if (seenRooms.has(roomKey)) {
            status = this.$store.state.locale.lang === "en" ? "Duplicate in file" : "ซ้ำในไฟล์";
            statusColor = "warning";
          } else if (existingRoomToBuilding.has(roomKey)) {
            const existingBuilding = existingRoomToBuilding.get(roomKey);
            status = existingBuilding === buildingKey
              ? (this.$store.state.locale.lang === "en" ? "Already exists" : "มีอยู่แล้ว")
              : (this.$store.state.locale.lang === "en" ? "Room used by another building" : "ชื่อห้องอยู่ในอาคารอื่นแล้ว");
            statusColor = existingBuilding === buildingKey ? "secondary" : "danger";
          } else {
            status = this.$store.state.locale.lang === "en" ? "Ready" : "พร้อมนำเข้า";
            statusColor = "success";
            importable = true;
          }

          if (roomKey) seenRooms.add(roomKey);
          return { ...row, status, statusColor, importable };
        });
    },

    async confirmBuildingsImport() {
      if (this.role !== "admin" || this.importableRows.length === 0) return;

      this.isImporting = true;
      let successCount = 0;
      let failCount = 0;

      for (const row of this.importableRows) {
        try {
          await this.$store.dispatch("building/addBuilding", {
            BUILDINGCODE: row.buildingCode,
            ROOMNAME: row.roomName
          });
          successCount++;
        } catch (error) {
          failCount++;
        }
      }

      await this.$store.dispatch("building/refreshBuildings");
      this.isImporting = false;
      this.showImportPreview = false;

      await Swal.fire({
        icon: failCount === 0 ? "success" : "warning",
        title: this.$store.state.locale.lang === "en" ? "Import completed" : "นำเข้าข้อมูลเสร็จสิ้น",
        html: this.$store.state.locale.lang === "en"
          ? `Imported ${successCount} row(s)<br>Failed ${failCount} row(s)`
          : `นำเข้าสำเร็จ ${successCount} รายการ<br>ไม่สำเร็จ ${failCount} รายการ`
      });

      this.importPreviewRows = [];
      this.importFileName = "";
    },

    closeImportPreview() {
      if (this.isImporting) return;
      this.showImportPreview = false;
      this.importPreviewRows = [];
      this.importFileName = "";
    },

    downloadBuildingsXlsx() {
      if (this.role !== "admin") return;

      const rows = [["BUILDINGCODE", "ROOMNAME"]];

      this.groupedBuildingsData.forEach((building) => {
        const rooms = Array.isArray(building.rooms) ? building.rooms : [];

        if (rooms.length === 0) {
          rows.push([building.buildingCode || "", ""]);
          return;
        }

        rooms.forEach((room) => {
          rows.push([
            building.buildingCode || "",
            room.ROOMNAME || ""
          ]);
        });
      });

      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      worksheet["!cols"] = [{ wch: 45 }, { wch: 45 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Buildings");

      const now = new Date();
      const dateStamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0")
      ].join("-");

      XLSX.writeFile(workbook, `Buildings_${dateStamp}.xlsx`);
    },

    openAddModal() {
      this.addModal = true;
    },

    async addDepartment() {
      try {
        await this.$store.dispatch("building/addBuilding", {
          BUILDINGCODE: this.newBuildingCode,
          ROOMNAME: this.newRoomName
        });

        Swal.fire(this.$t("mgmt.success"), this.$t("buildingMgmt.addOk"), "success");
        this.addModal = false;
        this.newBuildingCode = "";
        this.newRoomName = "";
      } catch (err) {
        Swal.fire(
          this.$t("mgmt.error"),
          err.response?.data?.message || this.$t("mgmt.addFail"),
          "error"
        );
      }
    },

    openEditBuildingModal(building) {
      this.editingOldBuildingName = building.buildingCode;
      this.editingNewBuildingName = building.buildingCode;
      // Deep copy rooms to avoid mutating store state directly
      this.editingRooms = JSON.parse(JSON.stringify(building.rooms || []));
      this.selectedRoomsToDelete = [];
      
      // If no rooms, add an empty one so the input box appears
      if (this.editingRooms.length === 0) {
        this.editingRooms.push({ ROOMNAME: '' });
      }
      
      this.editBuildingModal = true;
    },

    deleteSelectedRoomsInModal() {
       for (const i of this.selectedRoomsToDelete) {
           this.$set(this.editingRooms[i], 'isDeleted', true);
           this.editingRooms[i].ROOMNAME = ""; // ensures it gets deleted
       }
       this.selectedRoomsToDelete = [];
    },

    addEmptyRoom() {
      this.editingRooms.push({ ROOMNAME: '' });
    },

    async submitEditBuilding() {
      try {
        const promises = [];

        // 1. Edit Building Name if changed
        if (this.editingOldBuildingName !== this.editingNewBuildingName) {
           promises.push(this.$store.dispatch("building/editBuilding", {
            oldBuildingCode: this.editingOldBuildingName,
            newBuildingCode: this.editingNewBuildingName
          }));
        }

        // 2. Process Rooms (Edit, Add, or Delete)
        for (const room of this.editingRooms) {
           const roomName = room.ROOMNAME ? room.ROOMNAME.trim() : "";
           
           if (room._id || room.ROOMID) {
              // Existing room
              const roomId = room._id || room.ROOMID;

              if (roomName === "") {
                 // CASE: Name cleared -> Delete Room
                 promises.push(this.$store.dispatch("building/deleteRoom", roomId));
              } else {
                 // CASE: Name exists -> Update Room
                 promises.push(this.$store.dispatch("building/editRoom", {
                    roomId: roomId,
                    newRoomName: roomName
                 }));
              }
           } else {
              // New room (no ID)
              if (roomName !== "") {
                 // CASE: Has name -> Add new room
                 promises.push(this.$store.dispatch("building/addBuilding", {
                    BUILDINGCODE: this.editingNewBuildingName,
                    ROOMNAME: roomName
                 }));
              }
              // If new room has no name, we just ignore it (don't create)
           }
        }

        await Promise.all(promises);

        Swal.fire(this.$t("mgmt.success"), this.$t("mgmt.saveSaved"), "success");
        this.editBuildingModal = false;
      } catch (err) {
        console.error(err);
        Swal.fire(this.$t("mgmt.error"), err.response?.data?.message || this.$t("mgmt.saveFail"), "error");
      }
    },

    openEditModal(room) {
      this.editingRoomId = room._id;
      this.editingRoomName = room.ROOMNAME;
      this.editModal = true;
    },
    async submitEdit() {
      try {
        await this.$store.dispatch("building/editRoom", {
          roomId: this.editingRoomId,
          newRoomName: this.editingRoomName
        });
        Swal.fire(this.$t("mgmt.success"), this.$t("mgmt.editRoomSaved"), "success");
        this.editModal = false;
      } catch (err) {
        Swal.fire(this.$t("mgmt.error"), err.response?.data?.message || this.$t("mgmt.editFail"), "error");
      }

    },

    async confirmDelete(item) {
      const result = await Swal.fire({
        title: this.$t("mgmt.deleteBuildingTitle"),
        text: this.$t("buildingMgmt.deleteBuildingLine").replace("{name}", item.buildingCode),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e55353',
        cancelButtonColor: '#ced4da',
        confirmButtonText: this.$t("mgmt.delete"),
        cancelButtonText: this.$t("mgmt.cancel")
      });

      if (result.isConfirmed) {
        const result2 = await Swal.fire({
          title: this.$t("mgmt.deleteBuildingSure"),
          text: this.$t("buildingMgmt.buildingWillDeleteLine").replace("{name}", item.buildingCode),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#e55353',
          cancelButtonColor: '#ced4da',
          confirmButtonText: this.$t("mgmt.confirmDelete"),
          cancelButtonText: this.$t("mgmt.cancel")
        });

        if (result2.isConfirmed) {
          try {
            await this.$store.dispatch('building/deleteBuilding', item._id);
            Swal.fire(this.$t("mgmt.deleteOk"), this.$t("mgmt.deleteBuildingDone"), 'success');
          } catch (err) {
            Swal.fire(this.$t("mgmt.error"), this.$t("mgmt.cannotDelete"), 'error');
          }
        }
      }
    },

    toggleSelectAll(checked) {
      if (checked && this.groupedBuildingsData) {
        this.selectedBuildings = this.groupedBuildingsData.map(c => c._id);
      } else {
        this.selectedBuildings = [];
      }
    },

    async deleteMultipleBuildings() {
      const result = await Swal.fire({
        title: this.$t("mgmt.deleteBuildingBulkTitle"),
        text: this.$t("buildingMgmt.deleteBulkLine").replace("{n}", String(this.selectedBuildings.length)),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e55353',
        cancelButtonColor: '#ced4da',
        confirmButtonText: this.$t("mgmt.delete"),
        cancelButtonText: this.$t("mgmt.cancel")
      });

      if (result.isConfirmed) {
        const result2 = await Swal.fire({
          title: this.$t("mgmt.deleteBuildingSure"),
          text: this.$t("mgmt.buildingsSelectedWillDelete"),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#e55353',
          cancelButtonColor: '#ced4da',
          confirmButtonText: this.$t("mgmt.confirmDelete"),
          cancelButtonText: this.$t("mgmt.cancel")
        });

        if (result2.isConfirmed) {
          Swal.fire({
            title: this.$t("buildingMgmt.deletingTitle"),
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });

          let successCount = 0;
          let failCount = 0;

          for (const bId of this.selectedBuildings) {
            try {
              await this.$store.dispatch('building/deleteBuilding', bId);
              successCount++;
            } catch (err) {
              failCount++;
            }
          }

          Swal.fire({
            title: failCount === 0 ? this.$t("buildingMgmt.batchResultTitle") : this.$t("mgmt.doneTitle"),
            html: this.$t("buildingMgmt.batchResultHtml").replace("{ok}", String(successCount)) + (failCount > 0 ? `<br>${this.$t("buildingMgmt.batchFailLine").replace("{fail}", String(failCount))}` : ""),
            icon: failCount === 0 ? "success" : "warning"
          });
          
          this.selectedBuildings = [];
        }
      }
    },
  },

  mounted() {
    this.$store.dispatch("building/fetchBuildings");
    this.$store.dispatch("building/initSocket");
  }
};
</script>


<style scoped>
ul {
  list-style-type: disc;
}

.fixed-table {
  table-layout: fixed;
  width: 100%;
}

.fixed-table th,
.fixed-table td {
  text-align: left;
  vertical-align: middle;
  word-wrap: break-word;
}

.col-70 {
  width: 70%;
}

.col-30 {
  width: 30%;
}

.col-20 {
  width: 20%;
}

.col-10 {
  width: 10%;
}
</style>


<style>
.custom-pagination .page-item.active .page-link {
  background-color: #2ba9a5 !important;
  /* Example custom color */
  border-color: #2ba9a5 !important;
  color: #ffffff;
}

/* Target the text color of inactive buttons */
.custom-pagination .page-item .page-link {
  color: #414141;
  /* Dark gray for inactive text */
}

/* Target the hover state of buttons */
.custom-pagination .page-item:not(.active) .page-link:hover {
  background-color: #f0f0f0;
  /* Light gray on hover */
}
</style>
